<template>
  <div v-if="viewingEnvelope" class="party-status-table">
    <ElTable
      v-loading="loading"
      :data="viewingEnvelope.parties"
      :empty-text="$t('envelope.party.noParties')">
      <ElTableColumn :label="$t('envelope.party.partyTitle')">
        <template slot-scope="scope">
            {{ scope.row.title }}
        </template>
      </ElTableColumn>
      <ElTableColumn :label="$t('envelope.party.partyEmail')">
        <template slot-scope="scope">
          <div class="flex flex-wrap md:flex-nowrap">
            <div class="flex-grow truncate">
              {{ scope.row.email }}
            </div>
            <div class="flex-shrink-0" v-if="canUpdateParty(scope.row)">
              <ElTooltip
                effect="dark"
                :content="$t('envelope.party.edit')"
                placement="top-end">
                <ElButton
                  circle
                  type="outlinePrimary"
                  size="small"
                  icon="el-icon-edit"
                  @click="openPartyUpdateModal(scope.row)"
                  data-ec="envelope_status"
                  data-et="party_edit">
                </ElButton>
              </ElTooltip>
            </div>
          </div>
        </template>
      </ElTableColumn>
      <ElTableColumn :label="$t('envelope.party.partyStatus')">
        <template slot-scope="scope">
          {{ formattedPartyStatus(scope.row.status.computed_status, scope.row.status.computed_status_date) }}
        </template>
      </ElTableColumn>
      <ElTableColumn :label="$t('envelope.party.partyMessage')">
        <template slot-scope="scope">
            <ElPopover
              placement="top"
              width="400"
              trigger="hover"
              :content="scope.row.message">
              <template v-slot:reference>
                <span class="personal-message">
                  {{ scope.row.message }}
                </span>
              </template>
            </ElPopover>
        </template>
      </ElTableColumn>
      <ElTableColumn align="right" class="action-buttons">
        <template slot-scope="scope">
          <ElTooltip
            v-if="scope.row.status.computed_status === 'sent' && scope.row.signing_url"
            class="item"
            effect="dark"
            :content="$t('envelope.party.signEnvelope')"
            placement="top-end">
            <a
              :href="scope.row.signing_url"
              target="_blank"
              data-ec="envelope_status"
              data-et="sign_envelope">
              <ElButton
                circle
                type="success"
                size="small"
                icon="el-icon-document-checked">
              </ElButton>
            </a>
          </ElTooltip>
          <ElTooltip
            v-else-if="previewKey && scope.row.status.computed_status === 'sent'"
            effect="dark"
            :content="$t('envelope.party.previewSigning')"
            placement="top-end">
            <a
              :href="`${signingPageUrl}/preview/${viewingEnvelope.fingerprint}/${scope.row.status.fingerprint}#${previewKey}`"
              target="_blank"
              data-ec="envelope_status"
              data-et="preview_envelope">
              <ElButton
                circle
                type="outlineSecondary"
                size="small"
                icon="el-icon-search">
              </ElButton>
            </a>
          </ElTooltip>
        </template>
      </ElTableColumn>
    </ElTable>
    <VDynamicLoader
      viewURL="modals/party/UpdateEnvelopeParty"
      :data="{ envelope: viewingEnvelope, party }"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import localeDateFormatter from '@/utils/locale-date-formatter';
import VDynamicLoader from '@/components/DynamicLoader.vue';
import { logAmplitudeEvent } from '@/utils/analytics';

export default {
  name: 'PartyStatusTable',
  components: {
    VDynamicLoader,
  },
  data() {
    return {
      party: {},
      appV1Url: process.env.VUE_APP_APPV1,
      signingPageUrl: process.env.VUE_APP_SIGNING_PAGE,
    };
  },
  props: {
    loading: Boolean,
  },
  computed: {
    ...mapGetters({
      viewingEnvelope: 'envelopes/viewing',
      previewKey: 'auth/previewKey',
      canEditResource: 'envelopes/canEditResource',
    }),
  },
  mounted() {
    this.$store.dispatch('auth/GET_PREVIEW_KEY');
  },
  methods: {
    canUpdateParty(party) {
      const envelopeStatus = this.viewingEnvelope.status;
      const acceptablePartyStatus = ['unsent', 'unsentDraft', 'queued', 'unsentAwaiting', 'sent', 'bounced'];

      if (envelopeStatus !== 'sent' && envelopeStatus !== 'draft') {
        return false;
      }

      if (acceptablePartyStatus.includes(party.status.computed_status) && this.canEditResource(this.viewingEnvelope)) {
        return true;
      }

      return false;
    },
    /**
     * Get formatted party status string
     * Party status can only be one of: signed, bounced, sent, rejected, verify, queued, unsignedAwaiting or unsent
     */
    formattedPartyStatus(status, statusDate) {
      return this.$t(`envelope.partyStatus.${status}`, { date: localeDateFormatter('timestamp', statusDate) });
    },
    openPartyUpdateModal(party) {
      logAmplitudeEvent('partyStatusTable-party-update-modal-opened');
      this.$bus.$emit('closeModal');
      this.party = party;
      this.$bus.$emit('toggleModal', { name: 'party-update-modal', active: true });
    },
  },
};
</script>

<style scoped>
.el-table button.is-circle {
  margin-left: 5px;
}

@media (max-width: 665px) {
  .party-status-table {
    width: calc(100% - 2px); /* Fix for infinite table expansion bug https://github.com/ElemeFE/element/issues/11420 */
  }
}

.personal-message {
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}
</style>
